import axios from "axios";
import type { RichTextEditorRef } from "mui-tiptap/dist/RichTextEditor";
import { type RefObject, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../core/hooks/useStore";
import { useUserData } from "../../../core/hooks/useUserData";
import type { RootState } from "../../../core/store/store";
import type { ArticleResponse } from "../../../interfaces/article.interface";
import {
  ArticleGenerationStream,
  type ArticleGenerationEvent,
  type ArticleGenerationMode,
  type GenerationConnectionStatus,
} from "./article-generation-stream";
import editorSlice from "./editor.slice";
import { updateStreamingEditor } from "./streaming-editor";
import { truncateHtmlBySourceLength } from "./streaming-html";

const RENDER_INTERVAL_MS = 40;
const MIN_CHARACTERS_PER_SECOND = 80;
const MAX_CHARACTERS_PER_SECOND = 900;

const useGenerateArticle = (editorRef: RefObject<RichTextEditorRef>) => {
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const generationSessionRef = useRef(0);
  const animationFrameRef = useRef<number>();
  const streamRef = useRef<ArticleGenerationStream | null>(null);

  const closeCurrentStream = () => {
    generationSessionRef.current += 1;
    streamRef.current?.close();
    streamRef.current = null;
    if (animationFrameRef.current !== undefined) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
  };

  useEffect(() => closeCurrentStream, []);

  const setConnectionStatus = (
    status: GenerationConnectionStatus,
    message?: string
  ) => {
    dispatch(
      editorSlice.actions.setGenerationConnection({
        status,
        message: message || null,
      })
    );
  };

  const getArticle = async (articleId: string): Promise<void> => {
    try {
      const response = await axios.post<{ result: ArticleResponse }>(
        `${apiUrl}/article/get`,
        { id: articleId }
      );
      dispatch(editorSlice.actions.setArticle(response.data.result));
    } catch (error: any) {
      if (error.response?.data?.error?.message) {
        toast.error(`Error: ${error.response.data.error.message}`);
      } else if (error.request) {
        toast.error("Network error occurred while refreshing the article");
      } else {
        toast.error("Error getting article");
      }
    }
  };

  const updateEditor = (content: string) => {
    const editor = editorRef.current?.editor;
    if (editor) {
      updateStreamingEditor(editor, content);
    }
  };

  const runFetchGenerationLoop = (
    articleId: string,
    openedEditor: ArticleGenerationMode
  ) => {
    closeCurrentStream();

    const session = generationSessionRef.current;
    const currentUrl = window.location.href;
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    let latestContent = "";
    let targetSourceLength = 0;
    let revealedSourceLength = 0;
    let lastRenderedSourceLength = -1;
    let lastRenderTime = performance.now();
    let receivedContent = false;
    let finished = false;
    let pendingCompletion: ArticleGenerationEvent | null = null;

    const isCurrentSession = () =>
      generationSessionRef.current === session &&
      currentUrl === window.location.href;

    const finishGeneration = async (
      event: ArticleGenerationEvent,
      status: GenerationConnectionStatus = "idle"
    ) => {
      if (finished || !isCurrentSession()) {
        return;
      }
      finished = true;
      streamRef.current?.close();
      streamRef.current = null;
      if (animationFrameRef.current !== undefined) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      if (event.content) {
        updateEditor(event.content);
      }
      setConnectionStatus(status, event.message);
      await getArticle(articleId);
      userData.getUser();
    };

    const receiveEvent = (event: ArticleGenerationEvent) => {
      if (
        !isCurrentSession() ||
        event.articleId !== articleId ||
        event.mode !== openedEditor
      ) {
        return;
      }

      const formattedContent = (event.content || "")
        .split("\n")
        .join("")
        .split("<p></p>")
        .join("");

      if (formattedContent) {
        latestContent = formattedContent;
        targetSourceLength = formattedContent.length;

        if (!receivedContent) {
          receivedContent = true;
          revealedSourceLength = prefersReducedMotion ? targetSourceLength : 0;
          lastRenderTime = performance.now();
          updateEditor(
            truncateHtmlBySourceLength(latestContent, revealedSourceLength)
          );
          lastRenderedSourceLength = revealedSourceLength;
        } else if (revealedSourceLength > targetSourceLength) {
          revealedSourceLength = targetSourceLength;
        }
      }

      if (
        event.type === "complete" &&
        receivedContent &&
        revealedSourceLength < targetSourceLength &&
        !prefersReducedMotion
      ) {
        pendingCompletion = event;
      } else if (event.type === "complete" || event.type === "stopped") {
        void finishGeneration(event);
      } else if (event.type === "error") {
        void finishGeneration(event, "error");
      }
    };

    const renderNextFrame = (now: number) => {
      if (!isCurrentSession() || finished) {
        return;
      }

      const elapsed = now - lastRenderTime;
      const backlog = targetSourceLength - revealedSourceLength;

      if (backlog > 0 && elapsed >= RENDER_INTERVAL_MS) {
        if (prefersReducedMotion) {
          revealedSourceLength = targetSourceLength;
        } else {
          const charactersPerSecond = Math.min(
            MAX_CHARACTERS_PER_SECOND,
            Math.max(MIN_CHARACTERS_PER_SECOND, backlog * 6)
          );
          const charactersToReveal = Math.max(
            1,
            Math.round((charactersPerSecond * elapsed) / 1000)
          );
          revealedSourceLength = Math.min(
            targetSourceLength,
            revealedSourceLength + charactersToReveal
          );
        }
        lastRenderTime = now;

        if (revealedSourceLength !== lastRenderedSourceLength) {
          updateEditor(
            truncateHtmlBySourceLength(latestContent, revealedSourceLength)
          );
          lastRenderedSourceLength = revealedSourceLength;
        }
      }

      if (
        pendingCompletion &&
        revealedSourceLength >= targetSourceLength
      ) {
        const completion = pendingCompletion;
        pendingCompletion = null;
        void finishGeneration(completion);
        return;
      }

      animationFrameRef.current = window.requestAnimationFrame(renderNextFrame);
    };

    const streamUrl = `${apiUrl}/article/stream/${encodeURIComponent(
      articleId
    )}?mode=${openedEditor}`;
    const stream = new ArticleGenerationStream(streamUrl, {
      onEvent: receiveEvent,
      onStatusChange: setConnectionStatus,
    });
    streamRef.current = stream;

    if (!editorRef.current?.editor?.getText().trim()) {
      updateEditor("Thinking…");
    }
    animationFrameRef.current = window.requestAnimationFrame(renderNextFrame);
    stream.connect();
  };

  return { runFetchGenerationLoop };
};

export default useGenerateArticle;
